import { FC, useId } from 'react'

import { Icon } from '@/common/components/Icon'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export interface IFormGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children: JSX.Element
  wrapperClassName?: string
  labelWrapperClassName?: string
  inputWrapperClassName?: string
  labelClassName?: string
  label?: string | React.ReactNode
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  tooltip?: string
  labelStyle?: React.CSSProperties
  direction?: 'row' | 'column'
  justifyContent?: 'between' | 'center' | 'start' | 'end' | 'around'
  error?: string
  addon?: JSX.Element
  icon?: JSX.Element
}

const FormGroup: FC<IFormGroupProps> = ({
  wrapperClassName,
  labelClassName,
  label,
  labelProps,
  children,
  tooltip,
  labelWrapperClassName,
  inputWrapperClassName,
  labelStyle,
  direction = 'column',
  justifyContent = 'start',
  error,
  addon,
  icon,
  ...props
}) => {
  const toolTipId = useId()
  return (
    <div
      className={cn(
        'flex w-full gap-[10px]',
        direction === 'row' ? 'flex-row items-center' : 'flex-col',
        wrapperClassName,
      )}
      {...props}
    >
      <div
        className={cn(
          'flex items-center gap-2',
          justifyContent === 'between' && 'justify-between',
          justifyContent === 'center' && 'justify-center',
          justifyContent === 'start' && 'justify-start',
          justifyContent === 'end' && 'justify-end',
          justifyContent === 'around' && 'justify-around',
          direction === 'row' ? 'w-auto' : 'w-full',
          labelWrapperClassName,
        )}
        style={labelStyle}
      >
        {icon}
        <Label
          className={cn(
            'mb-0 flex flex-col !text-body-medium text-monochrome-1',
            labelClassName,
          )}
          {...labelProps}
        >
          {label}
        </Label>
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-describedby={toolTipId}
                  className="text-monochrome-1"
                >
                  <Icon
                    icon="info-filled"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent
                className="bg-monochrome-3-60-solid text-monochrome-1 border-monochrome-120"
                id={toolTipId}
              >
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {addon}
      </div>

      <div
        className={cn(
          'flex w-full items-start gap-2 min-w-0',
          inputWrapperClassName,
        )}
      >
        {children}
      </div>
      {/* {error && (
        <small className="text-body-small text-accent-3">{error}</small>
      )} */}
    </div>
  )
}

export default FormGroup
